import { useTranslation } from"react-i18next";
import { useState, useEffect, useRef } from"react";
import { flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Badge } from"@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from"@/components/ui/table";
import { 
 Plus, Scale, Calendar, AlertTriangle, CheckCircle, Clock, 
 FileText, Edit, Trash2, Eye, Gavel, Users, DollarSign,
 ChevronDown, ChevronUp, X
} from"lucide-react";
import { toast } from"sonner";
import { DeleteLawsuitDialog } from '@/components/attorney/DeleteLawsuitDialog';

const LawsuitsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [searchParams, setSearchParams] = useSearchParams();
 const selectedCaseId = searchParams.get('case');
 const [lawsuits, setLawsuits] = useState([]);
 const [summary, setSummary] = useState(null);
 const [loading, setLoading] = useState(true);
 const [createDialogOpen, setCreateDialogOpen] = useState(false);
 const [editDialogOpen, setEditDialogOpen] = useState(false);
 const [detailDialogOpen, setDetailDialogOpen] = useState(false);
 const [courtDateDialogOpen, setCourtDateDialogOpen] = useState(false);
 const [selectedLawsuit, setSelectedLawsuit] = useState(null);
 const [expandedLawsuit, setExpandedLawsuit] = useState(null);
 const [filterStatus, setFilterStatus] = useState("all");
 const [deleteTarget, setDeleteTarget] = useState(null);
 const [deletingLawsuit, setDeletingLawsuit] = useState(false);
 const [deleteError, setDeleteError] = useState('');
 const deleteInFlight = useRef(false);

 const [formData, setFormData] = useState({
 case_number:"",
 title:"",
 description:"",
 lawsuit_type:"civil",
 status:"active",
 plaintiff:"",
 defendant:"",
 our_role:"defendant",
 attorney_name:"",
 attorney_contact:"",
 opposing_attorney:"",
 court_name:"",
 judge_name:"",
 estimated_value:"",
 filed_date:"",
 notes:""
 });

 const [courtDateForm, setCourtDateForm] = useState({
 date:"",
 time:"",
 court_name:"",
 court_address:"",
 purpose:"",
 notes:""
 });

 useEffect(() => {
 loadData();
 }, [selectedCaseId]);

 const loadData = async (openCaseId = selectedCaseId) => {
 try {
 const [lawsuitsRes, summaryRes] = await Promise.all([
 axios.get("/lawsuits"),
 axios.get("/lawsuits/summary/overview")
 ]);
 setLawsuits(lawsuitsRes.data);
 setSummary(summaryRes.data);
 if (openCaseId) {
   const selected = lawsuitsRes.data.find(item => item.id === openCaseId);
   if (selected) { setSelectedLawsuit(selected); setDetailDialogOpen(true); }
   else { toast.error(t('attorneyDashboard.caseNotFound')); }
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleCreateLawsuit = async (e) => {
 e.preventDefault();
 try {
 await axios.post("/lawsuits", {
 ...formData,
 estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null
 });
 toast.success(t("messages.saveSuccess"));
 setCreateDialogOpen(false);
 resetForm();
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const openEditDialog = (lawsuit) => {
 setFormData({
 case_number: lawsuit.case_number ||"",
 title: lawsuit.title ||"",
 description: lawsuit.description ||"",
 lawsuit_type: lawsuit.lawsuit_type ||"civil",
 status: lawsuit.status ||"active",
 plaintiff: lawsuit.plaintiff ||"",
 defendant: lawsuit.defendant ||"",
 our_role: lawsuit.our_role ||"defendant",
 attorney_name: lawsuit.attorney_name ||"",
 attorney_contact: lawsuit.attorney_contact ||"",
 opposing_attorney: lawsuit.opposing_attorney ||"",
 court_name: lawsuit.court_name ||"",
 judge_name: lawsuit.judge_name ||"",
 estimated_value: lawsuit.estimated_value ||"",
 filed_date: lawsuit.filed_date ||"",
 notes: lawsuit.notes ||""
 });
 setSelectedLawsuit(lawsuit);
 setDetailDialogOpen(false);
 setEditDialogOpen(true);
 };

 const handleEditLawsuit = async (e) => {
 e.preventDefault();
 try {
 await axios.patch(`/lawsuits/${selectedLawsuit.id}`, {
 ...formData,
 estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null
 });
 toast.success(t("messages.updateSuccess"));
 setEditDialogOpen(false);
 resetForm();
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const handleUpdateLawsuit = async (lawsuitId, updates) => {
 try {
 await axios.patch(`/lawsuits/${lawsuitId}`, updates);
 toast.success(t("messages.updateSuccess"));
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const handleDeleteLawsuit = async () => {
 if (!deleteTarget || deleteInFlight.current) return;
 const lawsuitId = deleteTarget.id;
 deleteInFlight.current = true;
 flushSync(() => { setDeletingLawsuit(true); setDeleteError(''); });
 try {
 await axios.delete(`/lawsuits/${lawsuitId}`);
 toast.success(<span data-testid="lawsuit-delete-success">{t('lawsuitDeletion.success')}</span>);
 setDetailDialogOpen(false);
 setSelectedLawsuit(null);
 setDeleteTarget(null);
 setExpandedLawsuit(previous => previous === lawsuitId ? null : previous);
 setSearchParams(previous => { const next = new URLSearchParams(previous); next.delete('case'); return next; }, { replace: true });
 await loadData(null);
 } catch (error) {
 setDeleteError(t('lawsuitDeletion.failed'));
 }
 finally { deleteInFlight.current = false; setDeletingLawsuit(false); }
 };

 const handleAddCourtDate = async (e) => {
 e.preventDefault();
 if (!selectedLawsuit) return;
 try {
 await axios.post(`/lawsuits/${selectedLawsuit.id}/court-dates`, courtDateForm);
 toast.success(t("messages.saveSuccess"));
 setCourtDateDialogOpen(false);
 setCourtDateForm({ date:"", time:"", court_name:"", court_address:"", purpose:"", notes:""});
 loadData();
 // Refresh selected lawsuit
 const updated = await axios.get(`/lawsuits/${selectedLawsuit.id}`);
 setSelectedLawsuit(updated.data);
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const handleToggleCourtDateCompleted = async (lawsuitId, courtDateId, completed) => {
 try {
 await axios.patch(`/lawsuits/${lawsuitId}/court-dates/${courtDateId}?completed=${!completed}`);
 toast.success(completed ?"Court date marked as pending":"Court date marked as completed");
 loadData();
 if (selectedLawsuit) {
 const updated = await axios.get(`/lawsuits/${lawsuitId}`);
 setSelectedLawsuit(updated.data);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const handleDeleteCourtDate = async (lawsuitId, courtDateId) => {
 try {
 await axios.delete(`/lawsuits/${lawsuitId}/court-dates/${courtDateId}`);
 toast.success(t("messages.deleteSuccess"));
 loadData();
 if (selectedLawsuit) {
 const updated = await axios.get(`/lawsuits/${lawsuitId}`);
 setSelectedLawsuit(updated.data);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const resetForm = () => {
 setFormData({
 case_number:"",
 title:"",
 description:"",
 lawsuit_type:"civil",
 status:"active",
 plaintiff:"",
 defendant:"",
 our_role:"defendant",
 attorney_name:"",
 attorney_contact:"",
 opposing_attorney:"",
 court_name:"",
 judge_name:"",
 estimated_value:"",
 filed_date:"",
 notes:""
 });
 };

 const getStatusColor = (status) => {
 const colors = {
 active:"bg-rose-500/10 text-rose-700 border-red-200/60",
 pending:"bg-amber-500/10 text-amber-700 border-yellow-200/60",
 closed:"bg-white/60 text-slate-800 border-slate-200",
 won:"bg-emerald-500/10 text-emerald-700 border-green-200/60",
 lost:"bg-rose-500/10 text-rose-700 border-red-200/60",
 settled:"bg-blue-500/10 text-blue-700 border-blue-200/60"
 };
 return colors[status] || colors.pending;
 };

 const getTypeColor = (type) => {
 const colors = {
 civil:"bg-blue-500/10 text-blue-700",
 criminal:"bg-rose-500/10 text-rose-700",
 labor:"bg-amber-500/10 text-amber-700",
 property:"bg-emerald-500/10 text-emerald-700",
 contract:"bg-violet-500/10 text-violet-700",
 other:"bg-white/60 text-slate-800"
 };
 return colors[type] || colors.other;
 };

 const formatDate = (dateStr) => {
 if (!dateStr) return"N/A";
 try {
 return new Date(dateStr).toLocaleDateString("en-GB", {
 day:"2-digit",
 month:"short",
 year:"numeric"
 });
 } catch {
 return dateStr;
 }
 };

 const getDaysUntil = (dateStr) => {
 if (!dateStr) return null;
 const today = new Date();
 today.setHours(0, 0, 0, 0);
 const date = new Date(dateStr);
 date.setHours(0, 0, 0, 0);
 const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
 return diff;
 };

 const filteredLawsuits = lawsuits.filter(l => {
 if (filterStatus ==="all") return true;
 return l.status === filterStatus;
 });

 const getNextCourtDate = (lawsuit) => {
 const upcoming = (lawsuit.court_dates || [])
 .filter(cd => !cd.completed)
 .sort((a, b) => new Date(a.date) - new Date(b.date));
 return upcoming[0];
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading lawsuits...</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 min-w-0" data-testid="lawsuits-page">
 {/* Header */}
 <div className="flex flex-wrap items-center justify-between gap-4">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">
 Lawsuits & Legal Cases
 </h1>
 <p className="text-slate-600">Manage active lawsuits and track court dates</p>
 </div>
 <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
 <DialogTrigger asChild>
 <Button data-testid="lawsuit-create-button" className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/>
 New Lawsuit
 </Button>
 </DialogTrigger>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Create New Lawsuit</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleCreateLawsuit} className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Case Number *</Label>
 <Input
 value={formData.case_number}
 onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
 required
 placeholder="e.g., 2026-CV-12345"
 />
 </div>
 <div>
 <Label>Case Type</Label>
 <Select value={formData.lawsuit_type} onValueChange={(val) => setFormData({ ...formData, lawsuit_type: val })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="civil">Civil</SelectItem>
 <SelectItem value="criminal">Criminal</SelectItem>
 <SelectItem value="labor">Labor</SelectItem>
 <SelectItem value="property">Property</SelectItem>
 <SelectItem value="contract">Contract</SelectItem>
 <SelectItem value="other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div>
 <Label>Case Title *</Label>
 <Input
 value={formData.title}
 onChange={(e) => setFormData({ ...formData, title: e.target.value })}
 required
 placeholder="Brief title of the case"
 />
 </div>

 <div>
 <Label>Description</Label>
 <textarea
 className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 placeholder="Detailed description of the case..."
 />
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Plaintiff (Who is suing) *</Label>
 <Input
 value={formData.plaintiff}
 onChange={(e) => setFormData({ ...formData, plaintiff: e.target.value })}
 required
 />
 </div>
 <div>
 <Label>Defendant (Being sued) *</Label>
 <Input
 value={formData.defendant}
 onChange={(e) => setFormData({ ...formData, defendant: e.target.value })}
 required
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Our Role</Label>
 <Select value={formData.our_role} onValueChange={(val) => setFormData({ ...formData, our_role: val })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="plaintiff">Plaintiff (We are suing)</SelectItem>
 <SelectItem value="defendant">Defendant (Being sued)</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Status</Label>
 <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="active">Active</SelectItem>
 <SelectItem value="pending">Pending</SelectItem>
 <SelectItem value="closed">Closed</SelectItem>
 <SelectItem value="won">Won</SelectItem>
 <SelectItem value="lost">Lost</SelectItem>
 <SelectItem value="settled">Settled</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Our Attorney</Label>
 <Input
 value={formData.attorney_name}
 onChange={(e) => setFormData({ ...formData, attorney_name: e.target.value })}
 placeholder="Attorney name"
 />
 </div>
 <div>
 <Label>Attorney Contact</Label>
 <Input
 value={formData.attorney_contact}
 onChange={(e) => setFormData({ ...formData, attorney_contact: e.target.value })}
 placeholder="Phone or email"
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Opposing Attorney</Label>
 <Input
 value={formData.opposing_attorney}
 onChange={(e) => setFormData({ ...formData, opposing_attorney: e.target.value })}
 />
 </div>
 <div>
 <Label>Court Name</Label>
 <Input
 value={formData.court_name}
 onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
 placeholder="e.g., District Court of..."
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Judge Name</Label>
 <Input
 value={formData.judge_name}
 onChange={(e) => setFormData({ ...formData, judge_name: e.target.value })}
 placeholder="Name of the presiding judge"
 />
 </div>
 <div>
 <Label>Estimated Value (€)</Label>
 <Input
 type="number"
 value={formData.estimated_value}
 onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
 placeholder="Potential financial impact"
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Filed Date</Label>
 <Input
 type="date"
 value={formData.filed_date}
 onChange={(e) => setFormData({ ...formData, filed_date: e.target.value })}
 />
 </div>
 </div>

 <div>
 <Label>Notes</Label>
 <textarea
 className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 placeholder="Additional notes..."
 />
 </div>

 <div className="flex gap-3 pt-4 border-t">
 <Button type="button"variant="outline"onClick={() => setCreateDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 <Button type="submit"className="flex-1 bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 Create Lawsuit
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>

 {/* Summary Cards */}
 {summary && (
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 [&>div]:min-w-0 [&_p]:break-words">
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center gap-3">
 <Scale className="w-6 h-6 text-red-600"/>
 <div>
 <p className="text-2xl font-bold text-slate-900">{summary.active_count}</p>
 <p className="text-sm text-slate-600">Active Cases</p>
 </div>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center gap-3">
 <Clock className="w-6 h-6 text-yellow-600"/>
 <div>
 <p className="text-2xl font-bold text-slate-900">{summary.pending_count}</p>
 <p className="text-sm text-slate-600">Pending</p>
 </div>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center gap-3">
 <CheckCircle className="w-6 h-6 text-green-600"/>
 <div>
 <p className="text-2xl font-bold text-slate-900">{summary.closed_count}</p>
 <p className="text-sm text-slate-600">Closed</p>
 </div>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="p-4">
 <div className="flex items-center gap-3">
 <DollarSign className="w-6 h-6 text-blue-600"/>
 <div>
 <p className="text-2xl font-bold text-slate-900">€{(summary.total_estimated_value || 0).toLocaleString()}</p>
 <p className="text-sm text-slate-600">At Risk</p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>
 )}

 {/* Upcoming Court Dates Alert */}
 {summary?.upcoming_court_dates?.length > 0 && (
 <Card className="">
 <CardHeader className="pb-2">
 <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
 <Calendar className="h-6 w-6 text-amber-600"/>
 Upcoming Court Dates
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-2">
 {summary.upcoming_court_dates.slice(0, 5).map((cd, idx) => {
 const daysUntil = getDaysUntil(cd.date);
 return (
 <div key={idx} className="flex flex-wrap items-center justify-between gap-3 p-2 bg-white rounded border">
 <div className="min-w-0 break-words">
 <p className="font-medium text-slate-900">{cd.lawsuit_title}</p>
 <p className="text-sm text-slate-500">{cd.purpose} - {cd.court_name}</p>
 </div>
 <div className="text-right">
 <p className="font-semibold text-slate-900">{formatDate(cd.date)}</p>
 <p className={`text-sm ${daysUntil <= 3 ? 'text-red-600 font-semibold' : daysUntil <= 7 ? 'text-orange-600' : 'text-slate-600'}`}>
 {daysUntil === 0 ?"TODAY": daysUntil === 1 ?"Tomorrow": `${daysUntil} days`}
 </p>
 </div>
 </div>
 );
 })}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Filter and List */}
 <Card>
 <CardHeader>
 <div className="flex flex-wrap items-center justify-between gap-3">
 <CardTitle>All Lawsuits ({filteredLawsuits.length})</CardTitle>
 <Select value={filterStatus} onValueChange={setFilterStatus}>
 <SelectTrigger className="w-40" data-testid="lawsuit-status-filter">
 <SelectValue placeholder="Filter by status"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Status</SelectItem>
 <SelectItem value="active">Active</SelectItem>
 <SelectItem value="pending">Pending</SelectItem>
 <SelectItem value="closed">Closed</SelectItem>
 <SelectItem value="won">Won</SelectItem>
 <SelectItem value="lost">Lost</SelectItem>
 <SelectItem value="settled">Settled</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </CardHeader>
 <CardContent>
 {filteredLawsuits.length === 0 ? (
 <div className="text-center py-8 text-slate-500">
 <Scale className="w-12 h-12 mx-auto mb-3 opacity-50"/>
 <p>No lawsuits found</p>
 </div>
 ) : (
 <div className="space-y-3">
 {filteredLawsuits.map((lawsuit) => {
 const nextCourtDate = getNextCourtDate(lawsuit);
 const daysUntilCourt = nextCourtDate ? getDaysUntil(nextCourtDate.date) : null;
 const isExpanded = expandedLawsuit === lawsuit.id;

 return (
 <div key={lawsuit.id} className="border rounded-sm min-w-0" data-testid={`lawsuit-row-${lawsuit.id}`}>
 {/* Main Row */}
 <div 
 className="p-4 bg-white hover:bg-white/80 cursor-pointer"
 data-testid={`lawsuit-expand-${lawsuit.id}`}
 onClick={() => setExpandedLawsuit(isExpanded ? null : lawsuit.id)}
 >
 <div className="flex flex-wrap items-center justify-between gap-4">
 <div className="flex items-center gap-4 min-w-0 flex-1">
 <div className="flex items-center gap-2 shrink-0">
 {isExpanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
 <Gavel className="w-5 h-5 text-slate-400"/>
 </div>
 <div className="min-w-0 break-words">
 <div className="flex flex-wrap items-center gap-2">
 <span className="font-semibold text-slate-900" data-testid={`lawsuit-title-${lawsuit.id}`}>{lawsuit.title}</span>
 <Badge variant="outline"className={getStatusColor(lawsuit.status)}>
 {lawsuit.status}
 </Badge>
 <Badge className={getTypeColor(lawsuit.lawsuit_type)}>
 {lawsuit.lawsuit_type}
 </Badge>
 </div>
 <p className="text-sm text-slate-600">
 Case #{lawsuit.case_number} • {lawsuit.our_role ==="plaintiff"?"We vs":"vs"} {lawsuit.our_role ==="plaintiff"? lawsuit.defendant : lawsuit.plaintiff}
 </p>
 </div>
 </div>
 <div className="flex flex-wrap items-center gap-4 min-w-0">
 {nextCourtDate && (
 <div className="text-right">
 <p className="text-sm text-slate-600">Next Court Date</p>
 <p className={`font-semibold ${daysUntilCourt <= 3 ? 'text-red-600' : daysUntilCourt <= 7 ? 'text-orange-600' : 'text-slate-900'}`}>
 {formatDate(nextCourtDate.date)}
 </p>
 </div>
 )}
 {lawsuit.estimated_value && (
 <div className="text-right">
 <p className="text-sm text-slate-600">Est. Value</p>
 <p className="font-semibold text-slate-900">€{lawsuit.estimated_value.toLocaleString()}</p>
 </div>
 )}
 <Button
 size="sm"
 variant="outline"
 data-testid={`lawsuit-view-${lawsuit.id}`}
 onClick={(e) => {
 e.stopPropagation();
 setSelectedLawsuit(lawsuit);
 setDetailDialogOpen(true);
 }}
 >
 <Eye className="w-4 h-4 mr-1"/>
 View
 </Button>
 </div>
 </div>
 </div>

 {/* Expanded Court Dates */}
 {isExpanded && (
 <div className="border-t bg-white/50 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="font-semibold text-[#334155]">Court Dates</h4>
 <Button
 size="sm"
 variant="outline"
 onClick={(e) => {
 e.stopPropagation();
 setSelectedLawsuit(lawsuit);
 setCourtDateDialogOpen(true);
 }}
 >
 <Plus className="w-4 h-4 mr-1"/>
 Add Date
 </Button>
 </div>
 {lawsuit.court_dates?.length > 0 ? (
 <div className="space-y-2">
 {lawsuit.court_dates.map((cd) => (
 <div key={cd.id} className={`flex items-center justify-between p-3 rounded border ${cd.completed ? 'bg-white/60' : 'bg-white'}`}>
 <div className="flex items-center gap-3">
 <input
 type="checkbox"
 checked={cd.completed}
 onChange={() => handleToggleCourtDateCompleted(lawsuit.id, cd.id, cd.completed)}
 className="w-4 h-4"
 />
 <div className={cd.completed ? 'line-through text-slate-500' : ''}>
 <p className="font-medium">{cd.purpose}</p>
 <p className="text-sm text-slate-500">{cd.court_name} {cd.time && `at ${cd.time}`}</p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <span className={`font-semibold ${!cd.completed && getDaysUntil(cd.date) <= 3 ? 'text-red-600' : 'text-[#334155]'}`}>
 {formatDate(cd.date)}
 </span>
 <Button
 size="sm"
 variant="ghost"
 className="text-red-600 hover:text-red-700"
 onClick={() => handleDeleteCourtDate(lawsuit.id, cd.id)}
 >
 <X className="w-4 h-4"/>
 </Button>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-slate-500 text-sm">No court dates scheduled</p>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}
 </CardContent>
 </Card>
 </div>

 {/* Lawsuit Detail Dialog */}
 <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
 <DialogContent data-testid="lawsuit-detail-dialog" closeLabel={t('attorneyDashboard.close')} aria-describedby={undefined} className="w-[calc(100%_-_2rem)] max-w-2xl max-h-[90vh] overflow-y-auto break-words">
 <DialogHeader>
 <DialogTitle data-testid="lawsuit-detail-title" className="flex items-start gap-2 pr-6 min-w-0">
 <Gavel className="w-5 h-5 shrink-0"/>
 {selectedLawsuit?.title}
 </DialogTitle>
 </DialogHeader>
 {selectedLawsuit && (
 <div className="space-y-4">
 <div className="flex gap-2">
 <Badge variant="outline"className={getStatusColor(selectedLawsuit.status)}>
 {selectedLawsuit.status}
 </Badge>
 <Badge className={getTypeColor(selectedLawsuit.lawsuit_type)}>
 {selectedLawsuit.lawsuit_type}
 </Badge>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
 <div>
 <p className="text-sm text-slate-500">Case Number</p>
 <p className="font-medium" data-testid="lawsuit-detail-case-number">{selectedLawsuit.case_number}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Filed Date</p>
 <p className="font-medium">{formatDate(selectedLawsuit.filed_date)}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Plaintiff</p>
 <p className="font-medium">{selectedLawsuit.plaintiff}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Defendant</p>
 <p className="font-medium">{selectedLawsuit.defendant}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Our Attorney</p>
 <p className="font-medium">{selectedLawsuit.attorney_name ||"N/A"}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Opposing Attorney</p>
 <p className="font-medium">{selectedLawsuit.opposing_attorney ||"N/A"}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Court</p>
 <p className="font-medium">{selectedLawsuit.court_name ||"N/A"}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Judge</p>
 <p className="font-medium">{selectedLawsuit.judge_name ||"N/A"}</p>
 </div>
 <div>
 <p className="text-sm text-slate-500">Estimated Value</p>
 <p className="font-medium">€{(selectedLawsuit.estimated_value || 0).toLocaleString()}</p>
 </div>
 </div>

 {selectedLawsuit.description && (
 <div>
 <p className="text-sm text-slate-500">Description</p>
 <p className="text-[#334155]">{selectedLawsuit.description}</p>
 </div>
 )}

 {/* Court Dates in Detail */}
 <div className="border-t pt-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="font-semibold">Court Dates ({selectedLawsuit.court_dates?.length || 0})</h4>
 <Button
 size="sm"
 data-testid="lawsuit-detail-add-date"
 onClick={() => setCourtDateDialogOpen(true)}
 >
 <Plus className="w-4 h-4 mr-1"/>
 Add Date
 </Button>
 </div>
 {selectedLawsuit.court_dates?.length > 0 ? (
 <div className="space-y-2">
 {selectedLawsuit.court_dates.map((cd) => (
 <div key={cd.id} className={`p-3 rounded border ${cd.completed ? 'bg-white/50' : 'bg-white'}`}>
 <div className="flex items-center justify-between">
 <div>
 <p className={`font-medium ${cd.completed ? 'line-through text-slate-500' : ''}`}>
 {cd.purpose}
 </p>
 <p className="text-sm text-slate-500">{cd.court_name}</p>
 </div>
 <div className="text-right">
 <p className="font-semibold">{formatDate(cd.date)} {cd.time && `at ${cd.time}`}</p>
 {cd.completed ? (
 <Badge variant="outline"className="bg-emerald-500/10 text-emerald-700">Completed</Badge>
 ) : (
 <Badge variant="outline"className="bg-amber-500/10 text-amber-700">Pending</Badge>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-slate-500">No court dates scheduled</p>
 )}
 </div>

 {/* Actions */}
 <div className="flex flex-wrap gap-3 pt-4 border-t">
 <Button
 variant="outline"
 data-testid="lawsuit-detail-edit"
 onClick={() => openEditDialog(selectedLawsuit)}
 >
 <Edit className="w-4 h-4 mr-2"/>
 Edit Lawsuit
 </Button>
 {['owner', 'attorney'].includes(user.role) && !user.demoMode && (
 <Button
 variant="outline"
 className="text-red-600 hover:text-red-700 whitespace-normal h-auto min-h-10"
 data-testid="lawsuit-delete-button"
 onClick={() => { setDeleteError(''); setDeleteTarget(selectedLawsuit); }}
 >
 <Trash2 className="w-4 h-4 mr-2"/>
 {t('lawsuitDeletion.action')}
 </Button>
 )}
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>

 {/* Add Court Date Dialog */}
 <DeleteLawsuitDialog lawsuit={deleteTarget} deleting={deletingLawsuit} error={deleteError}
   onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteLawsuit} />
 <Dialog open={courtDateDialogOpen} onOpenChange={setCourtDateDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Add Court Date</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleAddCourtDate} className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Date *</Label>
 <Input
 type="date"
 value={courtDateForm.date}
 onChange={(e) => setCourtDateForm({ ...courtDateForm, date: e.target.value })}
 required
 />
 </div>
 <div>
 <Label>Time</Label>
 <Input
 type="time"
 value={courtDateForm.time}
 onChange={(e) => setCourtDateForm({ ...courtDateForm, time: e.target.value })}
 />
 </div>
 </div>
 <div>
 <Label>Purpose *</Label>
 <Input
 value={courtDateForm.purpose}
 onChange={(e) => setCourtDateForm({ ...courtDateForm, purpose: e.target.value })}
 required
 placeholder="e.g., Initial Hearing, Evidence Submission..."
 />
 </div>
 <div>
 <Label>Court Name *</Label>
 <Input
 value={courtDateForm.court_name}
 onChange={(e) => setCourtDateForm({ ...courtDateForm, court_name: e.target.value })}
 required
 placeholder="Court name"
 />
 </div>
 <div>
 <Label>Court Address</Label>
 <Input
 value={courtDateForm.court_address}
 onChange={(e) => setCourtDateForm({ ...courtDateForm, court_address: e.target.value })}
 placeholder="Full address"
 />
 </div>
 <div>
 <Label>Notes</Label>
 <textarea
 className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md"
 value={courtDateForm.notes}
 onChange={(e) => setCourtDateForm({ ...courtDateForm, notes: e.target.value })}
 placeholder="Additional notes..."
 />
 </div>
 <div className="flex gap-3 pt-4 border-t">
 <Button type="button"variant="outline"onClick={() => setCourtDateDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 <Button type="submit"className="flex-1 bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 Add Court Date
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>

 {/* Edit Lawsuit Dialog */}
 <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Edit Lawsuit</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleEditLawsuit} className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Case Number *</Label>
 <Input
 value={formData.case_number}
 onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
 required
 placeholder="e.g., 2026-CV-12345"
 />
 </div>
 <div>
 <Label>Case Type</Label>
 <Select value={formData.lawsuit_type} onValueChange={(val) => setFormData({ ...formData, lawsuit_type: val })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="civil">Civil</SelectItem>
 <SelectItem value="criminal">Criminal</SelectItem>
 <SelectItem value="labor">Labor</SelectItem>
 <SelectItem value="property">Property</SelectItem>
 <SelectItem value="contract">Contract</SelectItem>
 <SelectItem value="other">Other</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div>
 <Label>Case Title *</Label>
 <Input
 value={formData.title}
 onChange={(e) => setFormData({ ...formData, title: e.target.value })}
 required
 placeholder="Brief title of the case"
 />
 </div>

 <div>
 <Label>Description</Label>
 <textarea
 className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 placeholder="Detailed description of the case..."
 />
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Plaintiff (Who is suing) *</Label>
 <Input
 value={formData.plaintiff}
 onChange={(e) => setFormData({ ...formData, plaintiff: e.target.value })}
 required
 />
 </div>
 <div>
 <Label>Defendant (Being sued) *</Label>
 <Input
 value={formData.defendant}
 onChange={(e) => setFormData({ ...formData, defendant: e.target.value })}
 required
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Our Role</Label>
 <Select value={formData.our_role} onValueChange={(val) => setFormData({ ...formData, our_role: val })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="plaintiff">Plaintiff (We are suing)</SelectItem>
 <SelectItem value="defendant">Defendant (Being sued)</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Status</Label>
 <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="active">Active</SelectItem>
 <SelectItem value="pending">Pending</SelectItem>
 <SelectItem value="closed">Closed</SelectItem>
 <SelectItem value="won">Won</SelectItem>
 <SelectItem value="lost">Lost</SelectItem>
 <SelectItem value="settled">Settled</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Our Attorney</Label>
 <Input
 value={formData.attorney_name}
 onChange={(e) => setFormData({ ...formData, attorney_name: e.target.value })}
 placeholder="Attorney name"
 />
 </div>
 <div>
 <Label>Attorney Contact</Label>
 <Input
 value={formData.attorney_contact}
 onChange={(e) => setFormData({ ...formData, attorney_contact: e.target.value })}
 placeholder="Phone or email"
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Opposing Attorney</Label>
 <Input
 value={formData.opposing_attorney}
 onChange={(e) => setFormData({ ...formData, opposing_attorney: e.target.value })}
 />
 </div>
 <div>
 <Label>Court Name</Label>
 <Input
 value={formData.court_name}
 onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
 placeholder="e.g., District Court of..."
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Judge Name</Label>
 <Input
 value={formData.judge_name}
 onChange={(e) => setFormData({ ...formData, judge_name: e.target.value })}
 placeholder="Name of the presiding judge"
 />
 </div>
 <div>
 <Label>Estimated Value (€)</Label>
 <Input
 type="number"
 value={formData.estimated_value}
 onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
 placeholder="Potential financial impact"
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Filed Date</Label>
 <Input
 type="date"
 value={formData.filed_date}
 onChange={(e) => setFormData({ ...formData, filed_date: e.target.value })}
 />
 </div>
 </div>

 <div>
 <Label>Notes</Label>
 <textarea
 className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md"
 value={formData.notes}
 onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
 placeholder="Additional notes..."
 />
 </div>

 <div className="flex gap-3 pt-4 border-t">
 <Button type="button"variant="outline"onClick={() => { setEditDialogOpen(false); resetForm(); }} className="flex-1">
 Cancel
 </Button>
 <Button type="submit"className="flex-1 bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 Save Changes
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default LawsuitsPage;
